import { ref, computed, onMounted } from 'vue';
import { cloneDeep } from 'lodash';
import { useFilterByManager } from './useFilterByManager';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import type { DashboardWidget, GroupOrOrderOption } from '@/interfaces/Dashboard';

export function useWidgetSettings(widgetId: string) {
  const dashboardStore = useDashboardStore();
  const loaded = ref(false);
  const errors = ref(new Map<string, string | null>());
  const widgetBeforeEdit = ref<DashboardWidget>();

  const widget = computed(() => dashboardStore.getDashboardWidgetById(widgetId));

  const parameters = computed(() => widget.value?.defaultJsonProperties?.parameters);

  // Filter by manager
  const filterBy = computed(() => parameters.value?.filterBy);
  const { initializeFilterByManager } = useFilterByManager(filterBy);

  // Validation helpers
  const onFieldError = ({ name, error }: { name: string; error: string | null }) => {
    if (error) errors.value.set(name, error);
    else errors.value.delete(name);
  };

  const hasErrors = computed(() => errors.value.size > 0);

  // Initialize widget data
  const initializeWidgetData = async () => {
    if (!widget.value) return;

    await initializeFilterByManager();

    // Initialize groupBy and orderBy with data sources
    if (parameters.value?.groupBy && isFilterByDataSource(parameters.value.groupBy)) {
      const { data: values } = await dashboardStore.fetchDataSource(parameters.value.groupBy.dataSource!);
      const groupBy: GroupOrOrderOption = {
        ...parameters.value.groupBy,
        values: [{ label: 'No Grouping', value: '' }, ...values],
      };
      updateWidgetParameter('groupBy', groupBy);
    }

    if (parameters.value?.orderBy && isFilterByDataSource(parameters.value.orderBy)) {
      const { data: values } = await dashboardStore.fetchDataSource(parameters.value.orderBy.dataSource!);
      const orderBy: GroupOrOrderOption = {
        ...parameters.value.orderBy,
        values: [{ label: 'Default', value: null }, ...values],
      };
      updateWidgetParameter('orderBy', orderBy);
    }

    widgetBeforeEdit.value = cloneDeep(widget.value);
    loaded.value = true;
  };

  // Helper functions
  const isFilterByDataSource = (param: any): boolean => param && typeof param.dataSource === 'string';

  const updateWidgetParameter = (key: string, value: any) => {
    if (!widget.value?.defaultJsonProperties?.parameters) return;

    dashboardStore.updateDashboardWidget({
      ...widget.value,
      defaultJsonProperties: {
        ...widget.value.defaultJsonProperties,
        parameters: {
          ...widget.value.defaultJsonProperties.parameters,
          [key]: value,
        },
      },
    });
  };

  const resetWidget = (originalWidget: DashboardWidget) => {
    dashboardStore.updateDashboardWidget(originalWidget);
  };

  // Initialize on mount
  onMounted(initializeWidgetData);

  return {
    dashboardStore,
    widget,
    parameters,
    loaded,
    errors,
    onFieldError,
    hasErrors,
    widgetBeforeEdit,
    resetWidget,
    updateWidgetParameter,
    isFilterByDataSource,
  };
}
